import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiClient } from '@/lib/api-client';

// Platform-category user types (mirrors services/shared/use_case_gate.py's
// _PLATFORM_CATEGORY_USER_TYPES) whose access is resolved from a per-user
// assignment (auth_schema.user_use_case_assignments), not their tenant's
// config -- these personas sit on a placeholder platform tenant with no
// real tenant_use_case_config rows. super_admin/platform_admin are handled
// separately below (fully unscoped, no use-case check at all) and are NOT
// included here.
const USER_SCOPED_USER_TYPES = new Set(['back_office', 'claims_adjuster', 'verifier']);

export interface TenantUseCaseRecord {
  job_type_id: number;
  job_type_code: string;
  job_type_name?: string;
  enabled: boolean;
  settings?: Record<string, unknown>;
}

export interface TenantUseCasesResult {
  /** Set of enabled job_type_code values; undefined while loading or when no tenant scope applies. */
  enabledCodes: Set<string> | undefined;
  isLoading: boolean;
  error: unknown;
  /** True when the current session has no tenant scope (super_admin / unauthenticated). */
  unscoped: boolean;
  refetch: () => void;
  /** Returns true if enabled, false if disabled, undefined if data not yet loaded or fetch failed. */
  isUseCaseEnabled: (code: string) => boolean | undefined;
}

function readTenantScope(): { tenantId: string | null; userType: string; assignedUseCase: string | null } {
  if (typeof window === 'undefined') return { tenantId: null, userType: '', assignedUseCase: null };
  return {
    tenantId: window.localStorage.getItem('tenant_id'),
    userType: (window.localStorage.getItem('user_type') ?? '').toLowerCase(),
    assignedUseCase: window.localStorage.getItem('assigned_use_case'),
  };
}

export const tenantUseCasesKey = (tenantId: string) =>
  ['tenant-use-cases', tenantId] as const;

/**
 * Exposes the set of enabled `job_type_code` values for the current session, so
 * consumers can gate nav items and routes on whether a use case is available.
 *
 * Three resolution paths, by user_type:
 * - super_admin / platform_admin / unauthenticated: "unscoped" — no check applies,
 *   `enabledCodes` stays undefined (caller should treat this as unrestricted).
 * - back_office / claims_adjuster / verifier (platform-category, USER_SCOPED_USER_TYPES):
 *   resolved from `assigned_use_case` in localStorage (set at login from
 *   auth_schema.user_use_case_assignments) — these personas sit on a placeholder
 *   platform tenant with no real tenant_use_case_config rows, so a tenant-scoped
 *   fetch would always come back empty.
 * - everyone else (tenant-category, e.g. sub_agent_admin): fetched from the
 *   tenant's own configured use cases via `apiClient.getTenantUseCases`.
 *
 * `isUseCaseEnabled` is fail-open for tenant-scoped sessions: it returns undefined
 * (treated as enabled by UseCaseGuard) until data loads or when the fetch fails. For
 * user-scoped (platform-category) sessions it is definite: only the assigned use case
 * is enabled, every other code is false.
 */
export function useTenantUseCases(): TenantUseCasesResult {
  const { tenantId, userType, assignedUseCase } = readTenantScope();
  const isUserScoped = USER_SCOPED_USER_TYPES.has(userType);
  const unscoped = !isUserScoped && (!tenantId || userType === 'super_admin' || userType === 'platform_admin');

  // Platform-category users (back_office, claims_adjuster, verifier) were
  // already resolved once at login (see get_assigned_use_case_for_user() in
  // auth-service) and stored in localStorage — no query needed, and no
  // tenant-scoped fetch, since their placeholder tenant has no real config.
  const query = useQuery({
    queryKey: tenantUseCasesKey(tenantId ?? ''),
    queryFn: () => apiClient.getTenantUseCases(tenantId as string),
    enabled: !unscoped && !isUserScoped,
    staleTime: 5 * 60 * 1000,
    // On error, return undefined (fail-open: undefined treated as enabled by UseCaseGuard)
    throwOnError: false,
  });

  const rows = useMemo(() => {
    if (unscoped) return undefined;
    if (isUserScoped) {
      // Same record shape as the tenant path, so enabledCodes/isUseCaseEnabled need no special-casing.
      const assigned: TenantUseCaseRecord[] = assignedUseCase
        ? [{ job_type_id: 0, job_type_code: assignedUseCase, enabled: true }]
        : [];
      return assigned;
    }
    if (!query.data) return undefined;
    const data = query.data as TenantUseCaseRecord[];
    return Array.isArray(data) ? data : undefined;
  }, [unscoped, isUserScoped, assignedUseCase, query.data]);

  const enabledCodes = useMemo(() => {
    if (!rows) return undefined;
    return new Set(rows.filter((r) => r.enabled).map((r) => r.job_type_code));
  }, [rows]);

  function isUseCaseEnabled(code: string): boolean | undefined {
    if (!rows) return undefined;
    const entry = rows.find((uc) => uc.job_type_code === code);
    if (!entry) return isUserScoped ? false : undefined;
    return entry.enabled;
  }

  return {
    enabledCodes,
    isLoading: !unscoped && !isUserScoped && query.isLoading,
    error: isUserScoped ? undefined : query.error,
    unscoped,
    refetch: query.refetch,
    isUseCaseEnabled,
  };
}
